import { useState } from 'react'
import { Link } from 'react-router-dom'
import { track } from '../../lib/analytics'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

const REASONS = [
  'Question about SolaGrant',
  'Nonprofit discount request',
  'Billing or account help',
  'Partnership or press',
  'Other',
]

function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', reason: '', message: '', website: '' })
  const [status, setStatus] = useState('idle')

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (form.website) return
    setStatus('loading')
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          reason: form.reason,
          message: form.message,
        }),
      })
      if (!res.ok) throw new Error()
      track('contact_sent', { reason: form.reason || 'unspecified' })
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <p className="text-ink-700 text-sm py-2">
        Message sent. We'll reply within 1 business day.
      </p>
    )
  }

  const inputClass =
    'w-full rounded-lg border border-ink-300 bg-white px-4 py-2.5 text-base text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-0'

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {/* Honeypot — hidden from real users, attracts bots */}
      <input
        name="website"
        value={form.website}
        onChange={handleChange}
        tabIndex={-1}
        aria-hidden="true"
        className="absolute opacity-0 pointer-events-none h-0 w-0"
        autoComplete="off"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-ink-900 mb-1.5">
            Name <span aria-hidden="true">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            autoComplete="name"
            value={form.name}
            onChange={handleChange}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-ink-900 mb-1.5">
            Email <span aria-hidden="true">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={handleChange}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-reason" className="block text-sm font-medium text-ink-900 mb-1.5">
          Reason <span className="text-ink-500 font-normal">(optional)</span>
        </label>
        <select
          id="contact-reason"
          name="reason"
          value={form.reason}
          onChange={handleChange}
          className={inputClass}
        >
          <option value="">Select a reason</option>
          {REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-ink-900 mb-1.5">
          Message <span aria-hidden="true">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={4}
          required
          minLength={10}
          value={form.message}
          onChange={handleChange}
          className={`${inputClass} resize-none`}
        />
      </div>

      {status === 'error' && (
        <p className="text-sm text-red-600" role="alert">
          Something went wrong. Email us directly at{' '}
          <a href="mailto:team@solagrant.com" className="underline">
            team@solagrant.com
          </a>
          .
        </p>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full sm:w-auto inline-flex items-center justify-center min-h-11 px-6 rounded-lg bg-brand-600 text-white font-semibold text-base hover:bg-brand-700 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:opacity-50"
        >
          {status === 'loading' ? 'Sending…' : 'Send message'}
        </button>
        <p className="text-xs text-ink-500">
          Your message is used only to reply to you. See our{' '}
          <Link to="/legal" className="underline">
            Legal
          </Link>{' '}
          page for details.
        </p>
      </div>
    </form>
  )
}

export default function Footer() {
  return (
    <footer className="bg-ink-100">
      {/* Contact form */}
      <div className="px-4 sm:px-6 lg:px-8 border-b border-ink-300">
        <div className="mx-auto max-w-container py-16 md:py-20">
          <div className="max-w-xl">
            <h2 className="text-xl font-semibold text-ink-900 mb-1">Get in touch.</h2>
            <p className="text-sm text-ink-500 mb-6">
              Questions, nonprofit discount requests, or just saying hello.
            </p>
            <ContactForm />
          </div>
        </div>
      </div>

      {/* Slim nav */}
      <div className="px-4 sm:px-6 lg:px-8 border-b border-ink-300">
        <div className="mx-auto max-w-container py-6">
          <nav aria-label="Footer" className="flex flex-wrap gap-x-6 gap-y-3">
            <a href="/#product" className="text-sm text-ink-500 hover:text-ink-900 transition-colors">
              Product
            </a>
            <Link to="/pricing" className="text-sm text-ink-500 hover:text-ink-900 transition-colors">
              Pricing
            </Link>
            <Link to="/security" className="text-sm text-ink-500 hover:text-ink-900 transition-colors">
              Security
            </Link>
            <Link to="/legal" className="text-sm text-ink-500 hover:text-ink-900 transition-colors">
              Legal
            </Link>
            <a
              href="https://linkedin.com/company/solagrant"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-ink-500 hover:text-ink-900 transition-colors"
            >
              LinkedIn
            </a>
          </nav>
        </div>
      </div>

      {/* Bottom row */}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-container py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-ink-900 text-sm">SolaGrant</span>
              <span className="text-ink-500 text-sm">© 2025</span>
            </div>
            <Link to="/legal" className="text-sm text-ink-500 hover:text-ink-900 transition-colors">
              Legal
            </Link>
          </div>
          <p className="mt-2 text-xs text-ink-500">SOC 2 in progress · GDPR-ready · CCPA</p>
        </div>
      </div>
    </footer>
  )
}
